import React, { useEffect, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useI18n } from '../../../i18n';

interface XlsxViewerProps {
    fileUrl: string;
    highlightQuotes?: Array<{
        quote: string;
        id: string;
    }>;
    scrollToQuote?: string;
    onScrollComplete?: () => void;
}

interface SheetData {
    name: string;
    data: Array<Array<string | number>>;
}

// Normalize text for matching
const normalizeText = (text: string): string => {
    return text
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/\s+/g, ' ')
        .replace(/[""]/g, '"')
        .replace(/['']/g, "'")
        .replace(/[–-]/g, '-')
        .trim()
        .toLowerCase();
};

export const XlsxViewer: React.FC<XlsxViewerProps> = ({ fileUrl, highlightQuotes = [], scrollToQuote, onScrollComplete }) => {
    const { t } = useI18n();
    const [sheets, setSheets] = useState<SheetData[]>([]);
    const [activeSheet, setActiveSheet] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const lastScrollQuoteRef = useRef<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const loadDocument = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetch(fileUrl);
                if (!response.ok) {
                    throw new Error('Failed to fetch document');
                }

                const arrayBuffer = await response.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });

                const sheetsData: SheetData[] = workbook.SheetNames.map(sheetName => {
                    const sheet = workbook.Sheets[sheetName];
                    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as Array<Array<string | number>>;
                    return { name: sheetName, data };
                });

                setSheets(sheetsData);
                if (sheetsData.length > 0) {
                    setActiveSheet(sheetsData[0].name);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load document');
            } finally {
                setLoading(false);
            }
        };

        loadDocument();
    }, [fileUrl]);

    // Check if a cell matches any highlight quote
    const getCellHighlightInfo = (cellValue: string | number | undefined): { id: string; quote: string } | null => {
        if (cellValue === undefined || cellValue === null || cellValue === '') {
            return null;
        }

        const cellText = String(cellValue);
        const normalizedCellText = normalizeText(cellText);

        for (const { quote, id } of highlightQuotes) {
            if (!quote || !quote.trim()) continue;

            const normalizedQuote = normalizeText(quote);

            // Check various matching conditions
            const isMatch =
                // Exact containment
                normalizedCellText.includes(normalizedQuote) ||
                normalizedQuote.includes(normalizedCellText) ||
                // Word-based matching
                normalizedQuote.split(' ').filter(w => w.length > 2).some(word =>
                    normalizedCellText.includes(word)
                ) ||
                normalizedCellText.split(' ').filter(w => w.length > 2).some(word =>
                    normalizedQuote.includes(word)
                );

            if (isMatch) {
                return { id, quote };
            }
        }

        return null;
    };

    // Handle scrolling
    useEffect(() => {
        if (!scrollToQuote || scrollToQuote === lastScrollQuoteRef.current || loading) return;

        lastScrollQuoteRef.current = scrollToQuote;

        setTimeout(() => {
            const container = containerRef.current;
            if (!container) {
                onScrollComplete?.();
                return;
            }

            const highlightedCells = container.querySelectorAll('.source-highlight');
            const normalizedScrollQuote = normalizeText(scrollToQuote);
            let targetElement: Element | null = null;

            for (const cell of highlightedCells) {
                const cellQuote = cell.getAttribute('data-quote') || '';
                const normalizedCellQuote = normalizeText(cellQuote);

                if (normalizedCellQuote === normalizedScrollQuote ||
                    normalizedCellQuote.includes(normalizedScrollQuote) ||
                    normalizedScrollQuote.includes(normalizedCellQuote)) {
                    targetElement = cell;
                    break;
                }
            }

            // Fallback: use first highlight
            if (!targetElement && highlightedCells.length > 0) {
                targetElement = highlightedCells[0];
            }

            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                targetElement.classList.add('scroll-flash');
                setTimeout(() => targetElement?.classList.remove('scroll-flash'), 1500);
            }

            onScrollComplete?.();
        }, 100);
    }, [scrollToQuote, loading, onScrollComplete]);

    // Reset scroll tracking
    useEffect(() => {
        if (!scrollToQuote) {
            lastScrollQuoteRef.current = null;
        }
    }, [scrollToQuote]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">{t.playbooks.viewer.loadingSpreadsheet}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-red-500">{t.playbooks.viewer.error(error)}</div>
            </div>
        );
    }

    const activeSheetData = sheets.find(s => s.name === activeSheet);

    return (
        <div ref={containerRef} className="h-full flex flex-col bg-white">
            {/* Sheet tabs */}
            {sheets.length > 1 && (
                <div className="border-b border-gray-200 flex gap-1 px-4 pt-2 bg-gray-50">
                    {sheets.map(sheet => (
                        <button
                            key={sheet.name}
                            onClick={() => setActiveSheet(sheet.name)}
                            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                                activeSheet === sheet.name
                                    ? 'bg-white text-blue-600 border-t border-x border-gray-200'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {sheet.name}
                        </button>
                    ))}
                </div>
            )}

            {/* Sheet content */}
            <div className="flex-1 overflow-auto p-4">
                {activeSheetData && (
                    <div className="inline-block min-w-full">
                        <table className="border-collapse border border-gray-300">
                            <tbody>
                                {activeSheetData.data.map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {row.map((cell, colIndex) => {
                                            const highlightInfo = getCellHighlightInfo(cell);
                                            return (
                                                <td
                                                    key={colIndex}
                                                    data-source-id={highlightInfo?.id || undefined}
                                                    data-quote={highlightInfo?.quote || undefined}
                                                    className={`border border-gray-300 px-3 py-2 text-sm ${
                                                        highlightInfo ? 'source-highlight' : ''
                                                    }`}
                                                >
                                                    {cell}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <style>{`
                .source-highlight {
                    background-color: #fef08a !important;
                    transition: background-color 0.2s;
                }
                .source-highlight:hover {
                    background-color: #fde047 !important;
                }
                .source-highlight.scroll-flash {
                    animation: flash 1.5s ease-out;
                }
                @keyframes flash {
                    0%, 50% { background-color: #f97316 !important; }
                    100% { background-color: #fef08a !important; }
                }
            `}</style>
        </div>
    );
};
